import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Table, Form, Spinner, InputGroup, Row, Col, Badge } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
registerLocale('es', es);
import { debounce } from 'lodash';
import useToken from 'hooks/useToken';

// Format date to readable Spanish format
const formatDate = (dateString, includeTime = false) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    let str = `${date.getDate()} de ${months[date.getMonth()]} ${date.getFullYear()}`;
    if (includeTime) {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        str += ` ${hours}:${minutes}`;
    }
    return str;
};

const AkasiaPosUsers = () => {
    const { token } = useToken();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [userType, setUserType] = useState('All');
    const [userStatus, setUserStatus] = useState('All');
    const [dateFilterType, setDateFilterType] = useState('LastConnection');
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d;
    });
    const [endDate, setEndDate] = useState(new Date());
    const [appliedDateRange, setAppliedDateRange] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const pageSize = 25;

    const fetchData = useCallback(async (page, search, type, status, dateType, start, end, append = false) => {
        if (!token) return;

        try {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const response = await fetch('/api/AkasiaPosDashboard/GetUsers', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    pageNumber: page,
                    pageSize: pageSize,
                    searchTerm: search || null,
                    userType: type,
                    userStatus: status,
                    dateFilterType: dateType,
                    startDate: start ? start.toISOString() : null,
                    endDate: end ? end.toISOString() : null
                })
            });

            const result = await response.json();
            if (result.iResponseType === 1 && result.detalles) {
                const parsed = JSON.parse(result.detalles);

                // Always prefer table2 for data records
                const dataTable = parsed.table2 || [];

                // Get applied dates from table3
                const metaDates = parsed.table3 && parsed.table3[0];
                if (metaDates) {
                    setAppliedDateRange({
                        start: metaDates.dAppliedStartDate,
                        end: metaDates.dAppliedEndDate
                    });
                }

                if (dataTable.length > 0) {
                    if (append) {
                        setData(prev => [...prev, ...dataTable]);
                    } else {
                        setData(dataTable);
                    }

                    setHasMore(page * pageSize < (dataTable[0]?.iTotalCount || 0));
                } else {
                    if (!append) {
                        setData([]);
                    }
                    setHasMore(false);
                }
            } else {
                setError('Error loading users');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [token, pageSize]);

    // Debounced search
    const debouncedFetch = useCallback(
        debounce((search, type, status, dateType, start, end) => {
            setPageNumber(1);
            setData([]);
            fetchData(1, search, type, status, dateType, start, end, false);
        }, 500),
        [fetchData]
    );

    // Initial load and filter changes
    useEffect(() => {
        if (token) {
            debouncedFetch(searchTerm, userType, userStatus, dateFilterType, startDate, endDate);
        }
    }, [token, searchTerm, userType, userStatus, dateFilterType, startDate, endDate, debouncedFetch]);

    // Infinite scroll handler
    const handleScroll = useCallback(() => {
        const container = tableContainerRef.current;
        if (!container || loadingMore || !hasMore) return;

        const scrollPercentage = (container.scrollTop + container.clientHeight) / container.scrollHeight;
        if (scrollPercentage > 0.8) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            fetchData(nextPage, searchTerm, userType, userStatus, dateFilterType, startDate, endDate, true);
        }
    }, [loadingMore, hasMore, pageNumber, searchTerm, userType, userStatus, dateFilterType, startDate, endDate, fetchData]);

    useEffect(() => {
        const container = tableContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    return (
        <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-bottom">
                <div className="mb-3">
                    <div className="d-flex align-items-center w-100">
                        <h5 className="mb-0 fw-bold">
                            <i className="bi bi-people-fill text-primary me-2"></i>
                            Usuarios
                        </h5>
                        {appliedDateRange && (
                            <span className="ms-auto text-dark fw-medium">
                                <i className="bi bi-calendar3 me-1"></i>
                                {formatDate(appliedDateRange.start)} - {formatDate(appliedDateRange.end)}
                            </span>
                        )}
                    </div>
                    <p className="text-muted small mb-0 mt-1">Gestiona usuarios su ultima conexion y estado</p>
                </div>

                {/* Filters Row */}
                <Row className="g-2 mb-3">
                    <Col md={2}>
                        <Form.Select
                            size="sm"
                            value={userType}
                            onChange={(e) => setUserType(e.target.value)}
                            className="border-primary"
                        >
                            <option value="All">Tipos: Todos</option>
                            <option value="Trial">Prueba</option>
                            <option value="Paid">Pagado</option>
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Form.Select
                            size="sm"
                            value={userStatus}
                            onChange={(e) => setUserStatus(e.target.value)}
                            className="border-success"
                        >
                            <option value="All">Estado: Todos</option>
                            <option value="Active">Activo</option>
                            <option value="Inactive">Inactivo</option>
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Form.Select
                            size="sm"
                            value={dateFilterType}
                            onChange={(e) => setDateFilterType(e.target.value)}
                            className="border-warning"
                        >
                            <option value="LastConnection">Última Conexión</option>
                            <option value="CreationDate">Fecha Creación</option>
                        </Form.Select>
                    </Col>
                    <Col md={2} style={{ position: 'relative', zIndex: 1050 }}>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            className="form-control form-control-sm"
                            dateFormat="dd 'de' MMMM yyyy"
                            locale="es"
                            placeholderText="Desde"
                            todayButton="Hoy"
                            isClearable
                            popperPlacement="bottom-start"
                        />
                    </Col>
                    <Col md={2} style={{ position: 'relative', zIndex: 1050 }}>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            className="form-control form-control-sm"
                            dateFormat="dd 'de' MMMM yyyy"
                            locale="es"
                            placeholderText="Hasta"
                            todayButton="Hoy"
                            isClearable
                            popperPlacement="bottom-start"
                        />
                    </Col>
                    <Col md={2}>
                        <InputGroup size="sm">
                            <InputGroup.Text className="bg-white border-end-0">
                                <i className="bi bi-search text-muted"></i>
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Buscar..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border-start-0 ps-0"
                            />
                        </InputGroup>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body className="p-0">
                <div
                    ref={tableContainerRef}
                    className="table-responsive"
                    style={{ maxHeight: '600px', overflowY: 'auto' }}
                >
                    <Table hover className="mb-0">
                        <thead className="bg-light sticky-top" style={{ top: 0, zIndex: 1 }}>
                            <tr>
                                <th className="ps-4 py-3 border-bottom text-dark small fw-bold" style={{ width: '5%' }}>ID</th>
                                <th className="py-3 border-bottom text-dark small fw-bold" style={{ width: '25%' }}>Cliente</th>
                                <th className="py-3 border-bottom text-dark small fw-bold" style={{ width: '20%' }}>Usuario</th>
                                <th className="py-3 border-bottom text-dark small fw-bold" style={{ width: '25%' }}>Empresa</th>
                                <th className="py-3 border-bottom text-dark small fw-bold" style={{ width: '10%' }}>Fin Licencia</th>
                                <th className="py-3 border-bottom text-dark small fw-bold" style={{ width: '15%' }}>Última Actividad</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && data.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="text-dark mt-2 mb-0">Cargando usuarios...</p>
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-5">
                                        <i className="bi bi-exclamation-circle text-danger fs-1"></i>
                                        <p className="text-danger mt-2 mb-0">{error}</p>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-5">
                                        <i className="bi bi-inbox text-dark fs-1"></i>
                                        <p className="text-dark mt-2 mb-0">No se encontraron usuarios</p>
                                    </td>
                                </tr>
                            ) : (
                                <>
                                    {data.map((item, index) => (
                                        <tr key={`${item.vUsername}-${index}`}>
                                            <td className="ps-4 py-3 small">
                                                <a href={`/clientes/detalle/${item.idClienteAkasia}`} className="fw-bold">
                                                    #{item.idClienteAkasia || 'N/A'}
                                                </a>
                                            </td>
                                            <td className="py-3">
                                                <div className="fw-bold text-dark">
                                                    <a href={`/clientes/detalle/${item.idClienteAkasia}`} className="text-decoration-none text-dark">
                                                        {item.vFirstName && item.vLastName
                                                            ? `${item.vFirstName} ${item.vLastName}`
                                                            : item.vUsername}
                                                    </a>
                                                </div>
                                                {item.bIsSuperAdmin && (
                                                    <Badge bg="danger" className="mt-1" style={{ fontSize: '0.65rem' }}>
                                                        SUPER ADMIN
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="py-3 small text-dark">
                                                {item.vEmail}
                                            </td>
                                            <td className="py-3">
                                                <div className="fw-bold text-dark">{item.vNombreEmpresa || 'N/A'}</div>
                                                <div className="d-flex gap-1 mt-1">
                                                    <Badge bg={item.vTipoUsuario === 'Trial' ? 'warning' : 'success'} className="fw-normal" style={{ fontSize: '0.65rem' }}>
                                                        {item.vTipoUsuario === 'Trial' ? 'Prueba' : 'Pagado'}
                                                    </Badge>
                                                    <Badge bg={item.bIsActive ? 'success' : 'secondary'} className="fw-normal" style={{ fontSize: '0.65rem' }}>
                                                        {item.bIsActive ? 'Activo' : 'Inactivo'}
                                                    </Badge>
                                                </div>
                                            </td>
                                            <td className="py-3 small">
                                                {item.dFinLicenciaUso ? (
                                                    <span className={new Date(item.dFinLicenciaUso) < new Date() ? 'text-danger fw-bold' : 'text-dark'}>
                                                        {formatDate(item.dFinLicenciaUso)}
                                                    </span>
                                                ) : (
                                                    <span className="text-dark">-</span>
                                                )}
                                            </td>
                                            <td className="py-3 small">
                                                {item.dUltimaActividad ? (
                                                    <span className="text-dark">
                                                        <i className="bi bi-clock me-1"></i>
                                                        {formatDate(item.dUltimaActividad, true)}
                                                    </span>
                                                ) : (
                                                    <span className="text-dark">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </>
                            )}
                        </tbody>
                    </Table>
                    {loadingMore && (
                        <div className="text-center py-3 border-top">
                            <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                            <span className="text-muted small">Cargando más...</span>
                        </div>
                    )}
                </div>
            </Card.Body>
            {!loading && data.length > 0 && (
                <Card.Footer className="bg-light text-center text-muted small">
                    {hasMore ? (
                        <>
                            <i className="bi bi-arrow-down me-1"></i>
                            Desplázate para ver más
                        </>
                    ) : (
                        <>
                            <i className="bi bi-check-circle me-1"></i>
                            Todos los usuarios cargados
                        </>
                    )}
                </Card.Footer>
            )}
        </Card>
    );
};

export default AkasiaPosUsers;
